import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { debounceTime, Subject, Subscription } from 'rxjs';
import { ClBaseComponent } from '../cl-base/cl-base.component';
import { Resource } from 'src/app/components/catalogs/beamline-catalog/resource/resources';
import { ResourceType } from 'src/app/common/enumerations/enumerations';
import { User } from 'src/app/components/catalogs/user-catalog/services/user';

export interface ShieldingKeItem {
  id: number;
  keText: string;
  type: ResourceType;
  checked: boolean;
  index: number;
  user?: User | null;
  date?: Date | null;
}

@Component({
  selector: 'cl-shieldings-ke',
  templateUrl: './cl-shieldings-ke.component.html',
  styleUrls: ['./cl-shieldings-ke.component.scss']
})
export class ClShieldingsKeComponent extends ClBaseComponent implements OnInit, OnChanges, OnDestroy {

  @Input() resourceID?: number;

  shieldings: ShieldingKeItem[] = [];
  resourceType = ResourceType;

  private resourcesSubs?: Subscription;
  private shieldingSaveSubs?: Subscription;
  private shieldingSave$ = new Subject<void>();
  private pendingShieldingSave = false;
  private readonly shieldingSaveDebounceMs = 2000;

  /**
   * Loads shielding resources and prepares a debounced save queue for checkbox changes.
   */
  ngOnInit(): void {
    this.shieldingSaveSubs = this.shieldingSave$
      .pipe(debounceTime(this.shieldingSaveDebounceMs))
      .subscribe(() => this.saveShieldingValues());
    this.loadResources();
  }

  /**
   * Rebuilds the displayed shielding items when location, numbering, or saved values change.
   * @param changes Angular input changes from master-control.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['resourceID'] || changes['number'] || changes['valueString']) {
      // Status resets update the task text, so value changes must clear checked shielding items immediately.
      this.loadResources();
    }
  }

  override ngOnDestroy(): void {
    this.savePendingShieldingValues();
    this.shieldingSaveSubs?.unsubscribe();
    this.resourcesSubs?.unsubscribe();
    super.ngOnDestroy();
  }

  /**
   * Returns the user who owns the shielding checkbox group.
   * The first persisted checked item claims the group until every item is unchecked.
   */
  get sectionOwner(): User | null {
    return this.shieldings.find(item =>
      item.type === ResourceType.Shielding &&
      item.checked &&
      item.user?.id != null
    )?.user ?? null;
  }

  /**
   * Determines whether the signed-in user may update shielding checkboxes in this section.
   * @returns True when the group is unclaimed or owned by the signed-in user.
   */
  canCurrentUserUpdateSection(): boolean {
    const ownerID = this.sectionOwner?.id;
    const currentUserID = this.getCurrentUser()?.id;
    return ownerID == null || ownerID === currentUserID;
  }

  /**
   * Combines checklist-level disabling with the section's single-user ownership rule.
   * @returns True when the shielding checkbox must be read-only.
   */
  shieldingCheckboxDisabled(): boolean {
    return !!this.disabled || !this.canCurrentUserUpdateSection();
  }

  /**
   * Loads ordered shielding and note resources for the selected shutter.
   * Rebuilds the displayed items whenever the resource store changes.
   */
  loadResources() {
    this.resourcesSubs?.unsubscribe();
    this.resourcesSubs = this.store.select(state => state.Resources.data).subscribe((data: Resource[]) => {
      if (!data.length || !this.resourceID) return;

      const shutter = data.find(r => r.id === this.resourceID);

      let shieldingResources: Resource[] = [];
      // Shielding notes share the shielding relation order so they can render between checklist items.
      const shieldingItemTypes = [ResourceType.Shielding, ResourceType.ShieldingNote];
      const fromChildRelations = shutter?.childResources
        ?.filter(cr => shieldingItemTypes.includes(cr.childResourceType ?? 0))
        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0)) ?? [];

      if (fromChildRelations.length) {
        shieldingResources = fromChildRelations.reduce<Resource[]>((resources, relation) => {
          const resource = data.find(r => r.id === relation.childResourceID);
          if (resource) {
            resources.push({ ...resource, position: relation.position ?? resource.position });
          }
          return resources;
        }, []);
      } else {
        shieldingResources = data.filter(r =>
          shieldingItemTypes.includes(r.type ?? r.resourceTypeID ?? 0) &&
          r.parentResources?.some(pr => pr.parentResourceID === this.resourceID)
        ).sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
      }

      const saved: ShieldingKeItem[] = this.getValueObjects(this.valueString) ?? [];
      let counter = parseInt(this.number ?? '1');

      this.shieldings = shieldingResources.map(resource => {
        const savedItem = saved.find(s => s.id === resource.id);
        const type = (resource.type ?? resource.resourceTypeID ?? ResourceType.Shielding) as ResourceType;
        const isShielding = type === ResourceType.Shielding;
        return {
          id: resource.id ?? 0,
          keText: resource.keText ?? resource.name ?? '',
          type,
          // Notes retain their ordered position without consuming a checklist number.
          index: isShielding ? counter++ : 0,
          checked: isShielding ? savedItem?.checked ?? false : false,
          user: isShielding ? savedItem?.user ?? null : null,
          date: isShielding ? savedItem?.date ?? null : null,
        };
      });
      this.itemCountChanged.emit(this.shieldings.filter(item => item.type === ResourceType.Shielding).length);
    });
  }

  /**
   * Updates a shielding item's completion metadata and schedules a debounced save.
   * @param shielding Shielding item whose checkbox changed.
   * @param checked Checkbox state emitted by the child component.
   */
  checkboxChanged(shielding: ShieldingKeItem, checked: boolean) {
    // Guard the save path as well as the disabled UI against programmatic checkbox events.
    if (!this.canCurrentUserUpdateSection())
      return;

    shielding.checked = checked;
    shielding.user = checked ? this.getCurrentUser(true) ?? null : null;
    shielding.date = checked ? new Date() : null;
    this.pendingShieldingSave = true;
    this.shieldingSave$.next();
  }

  /**
   * Persists the current shielding checkbox values after the debounce interval.
   */
  private saveShieldingValues(): void {
    this.pendingShieldingSave = false;
    this.saveValues(this.shieldings.filter(item => item.type === ResourceType.Shielding));
  }

  /**
   * Flushes an unsaved shielding change before the component is destroyed.
   */
  private savePendingShieldingValues(): void {
    if (this.pendingShieldingSave)
      this.saveShieldingValues();
  }
}
